import React, { useState, useEffect, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  LinearProgress,
  Divider,
  IconButton,
  Tooltip
} from "@material-ui/core";
import { 
  TrendingUp,
  Send,
  CheckCircle,
  Error,
  Schedule,
  PhoneAndroid,
  BarChart as BarChartIcon,
  Refresh,
  GetApp,
  Visibility,
  EventAvailable as CampaignIcon
} from "@material-ui/icons";
import { format, subDays, startOfDay, endOfDay } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Chart as ChartJS, ArcElement, Tooltip as ChartTooltip, Legend, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title as ChartTitle } from "chart.js";
import { Doughnut, Bar, Line } from "react-chartjs-2";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import { useHistory } from "react-router-dom";

// Registrar componentes do Chart.js
ChartJS.register(
  ArcElement,
  ChartTooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ChartTitle
);

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
    marginBottom: theme.spacing(2),
  },
  filterContainer: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
  },
  summaryCard: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    transition: "transform 0.2s, box-shadow 0.2s",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: theme.shadows[8],
    },
  },
  cardIcon: {
    fontSize: 48,
    marginBottom: theme.spacing(1),
  },
  cardValue: {
    fontSize: "2.5rem",
    fontWeight: "bold",
    color: theme.palette.primary.main,
  },
  cardLabel: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
    textTransform: "uppercase",
    fontWeight: 500,
  },
  chartContainer: {
    height: 300,
    position: "relative",
    padding: theme.spacing(2),
  },
  tableContainer: {
    marginTop: theme.spacing(3),
  },
  statusChip: {
    fontWeight: "bold",
    color: "#fff",
  },
  completedChip: {
    backgroundColor: theme.palette.success.main,
  },
  activeChip: {
    backgroundColor: theme.palette.info.main,
  },
  cancelledChip: {
    backgroundColor: theme.palette.error.main,
  },
  scheduledChip: {
    backgroundColor: theme.palette.warning.main,
  },
  sectionTitle: {
    fontWeight: 600,
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  progressBar: {
    height: 10,
    borderRadius: 5,
  },
  filterButton: {
    marginLeft: theme.spacing(1),
  },
  metricCard: {
    textAlign: "center",
    padding: theme.spacing(3),
  },
  trendUp: {
    color: theme.palette.success.main,
  },
  trendDown: {
    color: theme.palette.error.main,
  },
}));

const CampaignsDashboard = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);

  // Estados para filtros
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30); // Últimos 30 dias
    return format(startOfDay(date), "yyyy-MM-dd'T'00:00");
  });
  const [endDate, setEndDate] = useState(
    format(endOfDay(new Date()), "yyyy-MM-dd'T'23:59")
  );
  const [loading, setLoading] = useState(false);

  // Estados para dados do dashboard
  const [dashboardData, setDashboardData] = useState({
    totalCampaigns: 0,
    activeCampaigns: 0,
    completedCampaigns: 0,
    cancelledCampaigns: 0,
    totalMessagesSent: 0,
    totalMessagesDelivered: 0,
    totalMessagesFailed: 0,
    totalMessagesPending: 0,
    totalResponses: 0,
    totalConfirmed: 0,
    responseRate: 0,
    confirmationRate: 0,
    deliveryRate: 0,
    failureRate: 0,
    totalUniqueRecipients: 0,
    avgMessagesPerCampaign: 0,
    avgDeliveryRate: 0,
    avgResponseRate: 0,
    campaignsByStatus: [],
    campaignsByDate: [],
    topCampaigns: [],
    recentCampaigns: [],
  });

  useEffect(() => {
    console.log("⚡ useEffect inicial - carregando dashboard");
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      console.log("📊 Buscando dados do dashboard META com filtros:", { startDate, endDate });
      console.log("📡 Fazendo requisição para: /campaigns-meta/dashboard-stats");
      
      // Converter datas para formato yyyy-MM-dd
      const initialDate = format(new Date(startDate), "yyyy-MM-dd");
      const finalDate = format(new Date(endDate), "yyyy-MM-dd");
      
      const { data } = await api.get("/campaigns-meta/dashboard-stats", {
        params: {
          initialDate,
          finalDate,
        },
      });
      
      console.log("✅ Dados META recebidos completos:", data);
      
      // Buscar também a lista de campanhas do período para preencher tabelas
      const { data: campaignsData } = await api.get("/campaigns-meta", {
        params: {
          initialDate,
          finalDate,
        },
      }).catch(() => ({ data: { records: [] } }));
      
      console.log("✅ Lista de campanhas:", campaignsData);
      
      // Mapear dados de campanhas META para o formato do dashboard
      const metaData = data?.geral || {};
      
      // Buscar stats individuais de cada campanha para Top 5
      const campaignsWithStats = await Promise.all(
        (campaignsData.records || []).slice(0, 20).map(async (campaign) => {
          try {
            const { data: stats } = await api.get(`/campaigns-meta/${campaign.id}/stats`);
            return {
              id: campaign.id,
              name: campaign.name,
              totalMessages: stats.totalMessages || 0,
              deliveredMessages: stats.deliveredMessages || 0,
              failedMessages: stats.failedMessages || 0,
              pendingMessages: stats.pendingMessages || 0,
              deliveryRate: stats.totalMessages > 0 ? ((stats.deliveredMessages / stats.totalMessages) * 100) : 0,
              status: campaign.status,
              scheduledAt: campaign.scheduledAt,
              responses: stats.respondedMessages || 0,
              responseRate: stats.responseRate || 0
            };
          } catch {
            return {
              id: campaign.id,
              name: campaign.name,
              totalMessages: 0,
              deliveredMessages: 0,
              failedMessages: 0,
              pendingMessages: 0,
              deliveryRate: 0,
              status: campaign.status,
              scheduledAt: campaign.scheduledAt,
              responses: 0,
              responseRate: 0
            };
          }
        })
      );
      
      // Ordenar por taxa de resposta (métrica mais importante) e depois por número de respostas
      const topCampaigns = campaignsWithStats
        .filter(c => c.totalMessages > 0 && c.responses > 0)
        .sort((a, b) => {
          // Primeiro critério: taxa de resposta
          if (b.responseRate !== a.responseRate) {
            return b.responseRate - a.responseRate;
          }
          // Segundo critério: número absoluto de respostas
          return b.responses - a.responses;
        })
        .slice(0, 5);
      
      // Campanhas recentes (últimas 10)
      const recentCampaigns = campaignsWithStats.slice(0, 10);
      
      // Calcular totais reais de falhas e pendentes somando de todas as campanhas
      const totalFailed = campaignsWithStats.reduce((sum, c) => sum + c.failedMessages, 0);
      const totalPending = campaignsWithStats.reduce((sum, c) => sum + c.pendingMessages, 0);
      const totalResponses = campaignsWithStats.reduce((sum, c) => sum + c.responses, 0);
      
      // Contar total de campanhas
      const totalCampaigns = campaignsData.count || campaignsData.records?.length || 0;
      
      // Calcular campanhas por status
      const statusCounts = (campaignsData.records || []).reduce((acc, camp) => {
        acc[camp.status] = (acc[camp.status] || 0) + 1;
        return acc;
      }, {});
      
      const campaignsByStatus = Object.entries(statusCounts).map(([status, count]) => ({
        status,
        count
      }));
      
      const mappedData = {
        totalCampaigns: totalCampaigns,
        activeCampaigns: statusCounts['EM_ANDAMENTO'] || 0,
        completedCampaigns: statusCounts['FINALIZADA'] || 0,
        cancelledCampaigns: statusCounts['CANCELADA'] || 0,
        totalMessagesSent: metaData.totalAgendado || 0,
        totalMessagesDelivered: metaData.totalEntregue || 0,
        totalMessagesFailed: totalFailed,
        totalMessagesPending: totalPending,
        totalResponses: totalResponses > 0 ? totalResponses : (metaData.totalRespostas || 0),
        totalConfirmed: 0,
        responseRate: metaData.taxaResposta || 0,
        confirmationRate: 0,
        deliveryRate: metaData.totalAgendado > 0 ? ((metaData.totalEntregue || 0) / metaData.totalAgendado) * 100 : 0,
        failureRate: metaData.totalAgendado > 0 ? (((metaData.totalAgendado || 0) - (metaData.totalEntregue || 0)) / metaData.totalAgendado) * 100 : 0,
        totalUniqueRecipients: metaData.totalAgendado || 0, // Aproximação
        avgMessagesPerCampaign: totalCampaigns > 0 ? Math.round((metaData.totalAgendado || 0) / totalCampaigns) : 0,
        avgDeliveryRate: metaData.totalAgendado > 0 ? ((metaData.totalEntregue || 0) / metaData.totalAgendado) * 100 : 0,
        avgResponseRate: metaData.taxaResposta || 0,
        campaignsByStatus: campaignsByStatus,
        campaignsByDate: [],
        topCampaigns: topCampaigns,
        recentCampaigns: recentCampaigns.map(c => ({
          id: c.id,
          name: c.name,
          status: c.status,
          totalMessages: c.totalMessages,
          deliveredMessages: c.deliveredMessages,
          responses: c.responses,
          responseRate: c.responseRate,
          scheduledAt: c.scheduledAt,
          completedAt: null
        }))
      };
      
      console.log("✅ Dados mapeados:", mappedData);
      setDashboardData(mappedData);
      toast.success("Dashboard META atualizado com sucesso!");
    } catch (error) {
      console.error("❌ Erro ao buscar dados do dashboard META:", error);
      console.error("Detalhes do erro:", error.response);
      toast.error("Erro ao carregar dados do dashboard: " + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
      console.log("🏁 Requisição finalizada");
    }
  };

  const handleQuickFilter = (days) => {
    console.log(`🕐 handleQuickFilter chamado com ${days} dias`);
    const end = endOfDay(new Date());
    const start = startOfDay(subDays(end, days - 1)); // -1 porque queremos incluir hoje
    setStartDate(format(start, "yyyy-MM-dd'T'00:00"));
    setEndDate(format(end, "yyyy-MM-dd'T'23:59"));
    console.log("Novas datas definidas:", { 
      start: format(start, "yyyy-MM-dd'T'00:00"), 
      end: format(end, "yyyy-MM-dd'T'23:59")
    });
  };

  const handleApplyFilter = () => {
    console.log("🔄 handleApplyFilter chamado!");
    console.log("Filtros atuais:", { startDate, endDate });
    fetchDashboardData();
  };

  const formatStatus = (status) => {
    const statusMap = {
      INATIVA: "Inativa",
      PROGRAMADA: "Programada",
      EM_ANDAMENTO: "Em Andamento",
      CANCELADA: "Cancelada",
      FINALIZADA: "Finalizada",
    };
    return statusMap[status] || status;
  };

  const getStatusChip = (status) => {
    let className = classes.statusChip;
    switch (status) {
      case "FINALIZADA":
        className += ` ${classes.completedChip}`;
        break;
      case "EM_ANDAMENTO":
        className += ` ${classes.activeChip}`;
        break;
      case "CANCELADA":
        className += ` ${classes.cancelledChip}`;
        break;
      case "PROGRAMADA":
        className += ` ${classes.scheduledChip}`;
        break;
      default:
        break;
    }
    return <Chip label={formatStatus(status)} size="small" className={className} />;
  };

  // Dados para gráfico de pizza - Status das mensagens
  const messageStatusChartData = {
    labels: ["Entregues", "Pendentes", "Falhas"],
    datasets: [
      {
        data: [
          dashboardData.totalMessagesDelivered,
          dashboardData.totalMessagesPending,
          dashboardData.totalMessagesFailed,
        ],
        backgroundColor: [
          "rgba(75, 192, 192, 0.8)",
          "rgba(255, 206, 86, 0.8)",
          "rgba(255, 99, 132, 0.8)",
        ],
        borderColor: [
          "rgba(75, 192, 192, 1)",
          "rgba(255, 206, 86, 1)",
          "rgba(255, 99, 132, 1)",
        ],
        borderWidth: 2,
      },
    ],
  };

  // Dados para gráfico de barras - Campanhas por status
  const campaignStatusChartData = {
    labels: dashboardData.campaignsByStatus.map((item) => formatStatus(item.status)),
    datasets: [
      {
        label: "Número de Campanhas",
        data: dashboardData.campaignsByStatus.map((item) => item.count),
        backgroundColor: "rgba(54, 162, 235, 0.6)",
        borderColor: "rgba(54, 162, 235, 1)",
        borderWidth: 1,
      },
    ],
  };

  // Dados para gráfico de linha - Campanhas ao longo do tempo
  const campaignTimelineChartData = {
    labels: dashboardData.campaignsByDate.map((item) =>
      format(new Date(item.date), "dd/MM", { locale: ptBR })
    ),
    datasets: [
      {
        label: "Campanhas Criadas",
        data: dashboardData.campaignsByDate.map((item) => item.count),
        fill: true,
        backgroundColor: "rgba(75, 192, 192, 0.2)",
        borderColor: "rgba(75, 192, 192, 1)",
        tension: 0.4,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
      },
    },
  };

  const exportToExcel = () => {
    try {
      const wb = XLSX.utils.book_new();

      // Aba 1: Resumo Geral
      const summaryData = [
        ["Dashboard de Campanhas"],
        [`Período: ${format(new Date(startDate), "dd/MM/yyyy HH:mm")} - ${format(new Date(endDate), "dd/MM/yyyy HH:mm")}`],
        [""],
        ["Métrica", "Valor"],
        ["Total de Campanhas", dashboardData.totalCampaigns],
        ["Campanhas Ativas", dashboardData.activeCampaigns],
        ["Campanhas Finalizadas", dashboardData.completedCampaigns],
        ["Campanhas Canceladas", dashboardData.cancelledCampaigns],
        [""],
        ["Total de Mensagens Enviadas", dashboardData.totalMessagesSent],
        ["Mensagens Entregues", dashboardData.totalMessagesDelivered],
        ["Mensagens Pendentes", dashboardData.totalMessagesPending],
        ["Mensagens Falhadas", dashboardData.totalMessagesFailed],
        [""],
        ["Respostas Recebidas", dashboardData.totalResponses],
        ["Confirmações Recebidas", dashboardData.totalConfirmed],
        ["Taxa de Resposta (%)", dashboardData.responseRate.toFixed(2)],
        ["Taxa de Confirmação (%)", dashboardData.confirmationRate.toFixed(2)],
        [""],
        ["Taxa de Entrega (%)", dashboardData.deliveryRate.toFixed(2)],
        ["Taxa de Falha (%)", dashboardData.failureRate.toFixed(2)],
        ["Destinatários Únicos", dashboardData.totalUniqueRecipients],
        ["Média de Mensagens por Campanha", dashboardData.avgMessagesPerCampaign],
        ["Taxa Média de Resposta (%)", dashboardData.avgResponseRate.toFixed(2)],
      ];
      const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(wb, wsSummary, "Resumo");

      // Aba 2: Top Campanhas
      const topCampaignsData = [
        ["Top 5 Campanhas"],
        [""],
        ["Nome", "Total Mensagens", "Mensagens Entregues", "Taxa de Entrega (%)", "Status", "Data Agendamento"],
        ...dashboardData.topCampaigns.map((campaign) => [
          campaign.name,
          campaign.totalMessages,
          campaign.deliveredMessages,
          campaign.deliveryRate.toFixed(2),
          formatStatus(campaign.status),
          format(new Date(campaign.scheduledAt), "dd/MM/yyyy HH:mm"),
        ]),
      ];
      const wsTopCampaigns = XLSX.utils.aoa_to_sheet(topCampaignsData);
      XLSX.utils.book_append_sheet(wb, wsTopCampaigns, "Top Campanhas");

      // Aba 3: Campanhas Recentes
      const recentCampaignsData = [
        ["Campanhas Recentes"],
        [""],
        ["Nome", "Status", "Total Mensagens", "Mensagens Entregues", "Data Agendamento", "Data Conclusão"],
        ...dashboardData.recentCampaigns.map((campaign) => [
          campaign.name,
          formatStatus(campaign.status),
          campaign.totalMessages,
          campaign.deliveredMessages,
          format(new Date(campaign.scheduledAt), "dd/MM/yyyy HH:mm"),
          campaign.completedAt ? format(new Date(campaign.completedAt), "dd/MM/yyyy HH:mm") : "-",
        ]),
      ];
      const wsRecentCampaigns = XLSX.utils.aoa_to_sheet(recentCampaignsData);
      XLSX.utils.book_append_sheet(wb, wsRecentCampaigns, "Campanhas Recentes");

      // Salvar arquivo
      const fileName = `dashboard_campanhas_${format(new Date(), "yyyyMMdd_HHmmss")}.xlsx`;
      XLSX.writeFile(wb, fileName);
      toast.success("Relatório exportado com sucesso!");
    } catch (error) {
      console.error("Erro ao exportar relatório:", error);
      toast.error("Erro ao exportar relatório");
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>
          <BarChartIcon style={{ marginRight: 8 }} />
          Dashboard de Campanhas META
        </Title>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        {/* Filtros */}
        <Paper className={classes.filterContainer} elevation={2}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                label="Data Início"
                type="datetime-local"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Data Fim"
                type="datetime-local"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box display="flex" gap={1} flexWrap="wrap">
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleQuickFilter(1)}
                >
                  Hoje
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleQuickFilter(7)}
                >
                  7 dias
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleQuickFilter(30)}
                >
                  30 dias
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<Refresh />}
                  onClick={handleApplyFilter}
                  className={classes.filterButton}
                >
                  Atualizar
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  size="small"
                  startIcon={<GetApp />}
                  onClick={exportToExcel}
                >
                  Exportar
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {loading && <LinearProgress />}

        {/* Cards de Resumo */}
        <Grid container spacing={3} style={{ marginTop: 16 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card className={classes.summaryCard}>
              <CardContent className={classes.metricCard}>
                <CampaignIcon className={classes.cardIcon} style={{ color: "#3f51b5" }} />
                <Typography className={classes.cardValue}>
                  {dashboardData.totalCampaigns}
                </Typography>
                <Typography className={classes.cardLabel}>Total de Campanhas</Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card className={classes.summaryCard}>
              <CardContent className={classes.metricCard}>
                <Send className={classes.cardIcon} style={{ color: "#4caf50" }} />
                <Typography className={classes.cardValue}>
                  {dashboardData.totalMessagesSent.toLocaleString()}
                </Typography>
                <Typography className={classes.cardLabel}>Mensagens Enviadas</Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card className={classes.summaryCard}>
              <CardContent className={classes.metricCard}>
                <CheckCircle className={classes.cardIcon} style={{ color: "#00bcd4" }} />
                <Typography className={classes.cardValue}>
                  {dashboardData.totalResponses.toLocaleString()}
                </Typography>
                <Typography className={classes.cardLabel}>Respostas Recebidas</Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card className={classes.summaryCard}>
              <CardContent className={classes.metricCard}>
                <TrendingUp className={classes.cardIcon} style={{ color: "#ff9800" }} />
                <Typography className={classes.cardValue}>
                  {dashboardData.responseRate.toFixed(1)}%
                </Typography>
                <Typography className={classes.cardLabel}>Taxa de Resposta</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Estatísticas Adicionais */}
        <Grid container spacing={3} style={{ marginTop: 16 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="textSecondary">
                  Taxa de Entrega
                </Typography>
                <Typography variant="h5" style={{ color: "#00bcd4" }}>
                  {dashboardData.deliveryRate.toFixed(1)}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="textSecondary">
                  Confirmações Recebidas
                </Typography>
                <Typography variant="h5" style={{ color: "#4caf50" }}>
                  {dashboardData.totalConfirmed.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="textSecondary">
                  Taxa Média de Resposta
                </Typography>
                <Typography variant="h5" style={{ color: "#ff9800" }}>
                  {dashboardData.avgResponseRate.toFixed(1)}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="textSecondary">
                  Destinátarios Únicos
                </Typography>
                <Typography variant="h5" style={{ color: "#9c27b0" }}>
                  {dashboardData.totalUniqueRecipients.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Gráficos */}
        <Typography className={classes.sectionTitle} variant="h6">
          <BarChartIcon />
          Análise Visual
        </Typography>
        <Divider />

        <Grid container spacing={3} style={{ marginTop: 16 }}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Status das Mensagens
                </Typography>
                <Box className={classes.chartContainer}>
                  <Doughnut data={messageStatusChartData} options={chartOptions} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Campanhas por Status
                </Typography>
                <Box className={classes.chartContainer}>
                  <Bar data={campaignStatusChartData} options={chartOptions} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Campanhas ao Longo do Tempo
                </Typography>
                <Box className={classes.chartContainer}>
                  <Line data={campaignTimelineChartData} options={chartOptions} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Top 5 Campanhas */}
        <Typography className={classes.sectionTitle} variant="h6">
          <TrendingUp />
          Top 5 Campanhas (Melhor Desempenho)
        </Typography>
        <Divider />

        <TableContainer component={Paper} style={{ marginTop: 16 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="right">Total Msgs</TableCell>
                <TableCell align="right">Respostas</TableCell>
                <TableCell align="right">Taxa Resposta</TableCell>
                <TableCell align="center">Data</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dashboardData.topCampaigns.map((campaign) => (
                <TableRow key={campaign.id}>
                  <TableCell>{campaign.name}</TableCell>
                  <TableCell align="center">{getStatusChip(campaign.status)}</TableCell>
                  <TableCell align="right">{campaign.totalMessages.toLocaleString()}</TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" style={{ fontWeight: 'bold', color: '#4caf50' }}>
                      {campaign.responses || 0}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Box display="flex" alignItems="center" justifyContent="flex-end">
                      <LinearProgress
                        variant="determinate"
                        value={campaign.responseRate}
                        style={{ width: 60, marginRight: 8 }}
                        className={classes.progressBar}
                        color="secondary"
                      />
                      <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                        {campaign.responseRate.toFixed(1)}%
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    {format(new Date(campaign.scheduledAt), "dd/MM/yyyy HH:mm")}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Ver Detalhes">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => history.push(`/campaign/${campaign.id}/report`)}
                      >
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {dashboardData.topCampaigns.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    Nenhuma campanha encontrada no período selecionado
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Campanhas Recentes */}
        <Typography className={classes.sectionTitle} variant="h6">
          <Schedule />
          Campanhas Recentes
        </Typography>
        <Divider />

        <TableContainer component={Paper} style={{ marginTop: 16 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="right">Total Msgs</TableCell>
                <TableCell align="right">Respostas</TableCell>
                <TableCell align="right">Taxa Resp.</TableCell>
                <TableCell align="center">Agendamento</TableCell>
                <TableCell align="center">Conclusão</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dashboardData.recentCampaigns.map((campaign) => (
                <TableRow key={campaign.id}>
                  <TableCell>{campaign.name}</TableCell>
                  <TableCell align="center">{getStatusChip(campaign.status)}</TableCell>
                  <TableCell align="right">{campaign.totalMessages.toLocaleString()}</TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" style={{ fontWeight: 'bold', color: '#4caf50' }}>
                      {campaign.responses || 0}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                      {campaign.responseRate ? campaign.responseRate.toFixed(1) : '0.0'}%
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {format(new Date(campaign.scheduledAt), "dd/MM/yyyy HH:mm")}
                  </TableCell>
                  <TableCell align="center">
                    {campaign.completedAt
                      ? format(new Date(campaign.completedAt), "dd/MM/yyyy HH:mm")
                      : "-"}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Ver Detalhes">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => history.push(`/campaign/${campaign.id}/report`)}
                      >
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {dashboardData.recentCampaigns.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    Nenhuma campanha encontrada no período selecionado
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </MainContainer>
  );
};

export default CampaignsDashboard;
